import * as React from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

interface PostcodeData {
  id: number;
  postcode: string;
  locality: string;
  long: number;
  lat: number;
}

interface LocationSearchStandaloneProps {
  basePath?: string; // Defaults to "/locations"
}

export function LocationSearchStandalone({
  basePath = "/locations",
}: LocationSearchStandaloneProps) {
  const [inputValue, setInputValue] = React.useState("");
  // The postcode dataset (~440KB raw) is split into its own chunk and only
  // fetched once the user starts typing, so the footer search box on every
  // page stays light.
  const [postcodeData, setPostcodeData] = React.useState<
    PostcodeData[] | null
  >(null);
  const loadRequested = React.useRef(false);

  React.useEffect(() => {
    if (inputValue.length === 0 || loadRequested.current) return;
    loadRequested.current = true;
    import("@/locations/vic_postcodes_stripped.json")
      .then((mod) => setPostcodeData(mod.default as PostcodeData[]))
      .catch((error) => {
        loadRequested.current = false;
        console.error("Locations search: failed to load postcodes", error);
      });
  }, [inputValue]);

  const filteredData = React.useMemo(() => {
    if (inputValue.length < 3 || !postcodeData) return [];
    const searchTerm = inputValue.toLowerCase();
    return postcodeData.filter(
      (item) =>
        item.locality.toLowerCase().includes(searchTerm) ||
        item.postcode.includes(searchTerm)
    );
  }, [inputValue, postcodeData]);

  const handleSelect = (item: PostcodeData) => {
    const lat = item.lat;
    const lng = item.long;
    const url = `${basePath}?lat=${encodeURIComponent(
      lat
    )}&lng=${encodeURIComponent(lng)}&suburb=${encodeURIComponent(
      item.locality
    )}&postcode=${encodeURIComponent(item.postcode)}`;
    window.location.assign(url);
  };

  return (
    <div className="relative z-50">
      {/* Visually hidden label for assistive technologies */}
      <label htmlFor="location-search-input" className="sr-only">
        Search by suburb or postcode
      </label>
      <p id="location-search-hint" className="sr-only">
        Type at least 3 characters to see suggestions. Use arrow keys to
        navigate results and Enter to select.
      </p>
      <Command
        className="bg-brand-primary rounded-full border-2 border-brand-tertiary-1 focus-within:outline-none focus-within:shadow-[0_0_12px_0_rgba(188,232,202,0.40),0_1px_2px_0_rgba(147,213,170,0.25),0_1px_3px_1px_rgba(147,213,170,0.25)] focus-within:bg-white text-brand-primary"
        aria-label="Location search"
        role="search"
      >
        <CommandInput
          placeholder="Search suburb or postcode..."
          value={inputValue}
          onValueChange={setInputValue}
          id="location-search-input"
          aria-label="Search by suburb or postcode"
          aria-describedby="location-search-hint"
          role="combobox"
          aria-haspopup="listbox"
          aria-controls="location-search-listbox"
          aria-expanded={inputValue.length >= 3}
          aria-autocomplete="list"
          autoComplete="off"
          className="text-brand-tertiary-1 placeholder:text-brand-tertiary-1 focus:!text-brand-primary-alt focus:placeholder:!text-brand-primary-alt focus-visible:outline-none w-full h-[56px] text-md"
          wrapperClassName="text-brand-tertiary-1 focus-within:text-brand-primary-alt"
          iconPosition="right"
        />
        {inputValue.length >= 3 && (
          <CommandList
            id="location-search-listbox"
            role="listbox"
            aria-label="Search results"
            className="max-h-[200px] absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
          >
            <CommandEmpty role="status" aria-live="polite">
              {postcodeData ? "No locations found." : "Loading locations..."}
            </CommandEmpty>
            {filteredData.length > 0 && (
              <CommandGroup role="group" aria-label="Matching locations">
                {filteredData.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={`${item.locality}-${item.postcode}`}
                    onSelect={() => handleSelect(item)}
                    role="option"
                    id={`location-option-${item.id}`}
                    aria-label={`${item.locality} - Postcode ${item.postcode}`}
                  >
                    <span className="text-brand-primary">
                      {item.locality} - Postcode: {item.postcode}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        )}
      </Command>
    </div>
  );
}
